import React, { useLayoutEffect, useRef, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import { SvgIconProps } from '@mui/material/SvgIcon'

import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'

import colors from '@/assets/scss/_themes-vars.module.scss'

import { closedMixin, openedMixin } from '@/utils/mui'

export const sidebarWidthExpanded = 230
export const sidebarWidthCollapsed = 60

export type SidebarProps = {
  menuItems: {
    icon: React.ComponentType<SvgIconProps>
    id: string
    label: string
  }[]
  onSelect?: (menuItemId: string) => void
  renderFooter?: (props: { expanded: boolean }) => JSX.Element | null
  selectedMenuItem: string
  anchor?: 'left' | 'right' | 'top' | 'bottom'
  id?: string
}

export function Sidebar(props: SidebarProps) {
  const { menuItems, onSelect, renderFooter, selectedMenuItem, anchor = 'left', id } = props
  const { t } = useTranslation()
  const theme = useTheme()
  const { height: menuItemsWrapperHeight, ref: menuItemsWrapperRef } = useResizeDetector()
  const menuWrapperRef = useRef<HTMLDivElement>(null)

  const [expanded, setExpanded] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [menuItemSpacing, setMenuItemSpacing] = useState<number>(4)

  useLayoutEffect(() => {
    if (menuWrapperRef.current && menuItemsWrapperHeight) {
      const menuItemsBoxWrapperHeight = menuWrapperRef.current.getBoundingClientRect().height
      if (menuItemsWrapperHeight > menuItemsBoxWrapperHeight) setMenuItemSpacing((prevMenuItemSpacing) => Math.max(prevMenuItemSpacing - 1, 0))
    }
  }, [menuItemsWrapperHeight])

  const handleExpandSidebar = () => {
    setExpanded(true)
  }

  const handleCollapseSidebar = () => {
    setExpanded(false)
  }

  const handleSelectMenuItem = (id: string, index: number) => () => {
    setProgress((100 / (menuItems.length - 1)) * index)
    onSelect?.(id)
  }

  return (
    <Drawer
      data-testid="diagnostic-layout-sidebar"
      variant="permanent"
      sx={[
        {
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: anchor === 'left' ? { borderRight: 0 } : { borderLeft: 0 },
        },
        expanded && {
          ...openedMixin(sidebarWidthExpanded)(theme),
          '& .MuiDrawer-paper': openedMixin(sidebarWidthExpanded)(theme),
        },
        !expanded && {
          ...closedMixin(sidebarWidthCollapsed)(theme),
          '& .MuiDrawer-paper': closedMixin(sidebarWidthCollapsed)(theme),
        },
      ]}
      PaperProps={{
        elevation: 0,
      }}
      anchor={anchor}
      id={id}
    >
      <Stack sx={{ height: '100%' }}>
        <Toolbar />
        <Stack direction="row" alignItems="center" justifyContent={expanded ? 'end' : 'center'} sx={{ height: '60px', p: '14px 10px' }}>
          {expanded ? (
            <Button
              onClick={handleCollapseSidebar}
              size="small"
              startIcon={() => {
                if (anchor === 'left') {
                  return <ArrowBackIosNewRoundedIcon fontSize="small" />
                } else if (anchor === 'right') {
                  return <ArrowForwardIosRoundedIcon fontSize="small" />
                }
              }}
              variant="contained"
              sx={{ borderRadius: '35px' }}
            >
              {t('common:button.minimize.label')}
            </Button>
          ) : (
            <IconButton id="expand-button" color="primary" onClick={handleExpandSidebar} size="small" sx={{ mt: '1px', mb: '1px' }}>
              {anchor === 'left' && <ArrowForwardIosRoundedIcon fontSize="small" />}
              {anchor === 'right' && <ArrowBackIosNewRoundedIcon fontSize="small" />}
            </IconButton>
          )}
        </Stack>
        <Stack ref={menuWrapperRef} justifyContent="center" sx={{ flex: 1, p: '0 10px', overflowX: 'hidden', overflowY: 'auto' }}>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '19px',
                transform: 'translateY(-50%)',
                width: '2px',
                height: 'calc(100% - 32px)',
                backgroundColor: colors.gray200,
                '& .MuiLinearProgress-bar': {
                  transform: `translateY(${-100 + progress}%) !important`,
                },
              }}
            />
            <Stack ref={menuItemsWrapperRef} spacing={menuItemSpacing}>
              {menuItems.map((menuItem, index) => {
                const Icon = menuItem.icon
                const button = (
                  <Button
                    id={menuItem.id}
                    color="primary"
                    fullWidth
                    onClick={handleSelectMenuItem(menuItem.id, index)}
                    startIcon={<Icon htmlColor={selectedMenuItem === menuItem.id ? theme.palette.primary.main : undefined} />}
                    sx={[
                      {
                        fontSize: '12px',
                        fontWeight: 600,
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase',
                        justifyContent: 'start',
                        minWidth: 'unset',
                        pl: '4px',
                        pr: '4px',
                        '& .MuiButton-startIcon': {
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          background: theme.palette.common.white,
                          border: `2px solid ${theme.palette.primary.main}`,
                          borderRadius: '50%',
                          margin: 0,
                        },
                      },
                      selectedMenuItem !== menuItem.id && {
                        color: colors.gray500,
                        '& .MuiButton-startIcon': {
                          borderColor: colors.gray300,
                        },
                      },
                    ]}
                  >
                    <Box component="span" sx={{ position: 'absolute', left: '50px', textAlign: 'left' }}>
                      {menuItem.label}
                    </Box>
                  </Button>
                )

                return expanded ? (
                  <Box key={menuItem.id}>{button}</Box>
                ) : (
                  <Tooltip key={menuItem.id} arrow title={menuItem.label} placement="right">
                    {button}
                  </Tooltip>
                )
              })}
            </Stack>
          </Box>
        </Stack>
        <Stack>{renderFooter?.({ expanded })}</Stack>
      </Stack>
    </Drawer>
  )
}

export default Sidebar
